import React, { useState, useCallback, useEffect } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PlantafelService, { LaneTeam } from '../services/PlantafelService';
import ProjectService, { Project } from '../../contacts/services/ProjectService';
import { pdebugError } from '../utils/plantafelDebug';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface CreateJobModalProps {
  isOpen: boolean;
  defaultTeamId: number;
  defaultDateStr: string;
  teams: LaneTeam[];
  onClose: () => void;
  onCreated: (createdShift: import('../../shifts/services/ShiftService').Shift) => void;
}

const PRIORITY_OPTIONS = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'URGENT', label: 'Eilig' },
  { value: 'EMERGENCY', label: 'Notfall' },
];

export default function CreateJobModal({
  isOpen,
  defaultTeamId,
  defaultDateStr,
  teams,
  onClose,
  onCreated,
}: CreateJobModalProps): React.JSX.Element | null {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [projectId, setProjectId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'NORMAL' | 'URGENT' | 'EMERGENCY'>('NORMAL');
  const [teamId, setTeamId] = useState(defaultTeamId);
  const [dateStr, setDateStr] = useState(defaultDateStr);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setLoadingProjects(true);
    ProjectService.list()
      .then((data) => {
        setProjects(data);
      })
      .catch((err) => {
        pdebugError('Failed to load projects:', err);
        toast.error('Fehler beim Laden der Projekte');
      })
      .finally(() => {
        setLoadingProjects(false);
      });
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTeamId(defaultTeamId);
      setDateStr(defaultDateStr);
      setProjectId(null);
      setTitle('');
      setDescription('');
      setPriority('NORMAL');
    }
  }, [isOpen, defaultTeamId, defaultDateStr]);

  const handleSave = useCallback(async () => {
    if (!projectId) {
      toast.warning('Bitte Projekt auswählen');
      return;
    }
    if (!title.trim()) {
      toast.warning('Bitte Titel eingeben');
      return;
    }

    setSaving(true);
    try {
      const createdShift = await PlantafelService.createJob({
        project_id: projectId,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        work_area_team_id: teamId,
        mode: 'FULL_DAY',
        start_date: dateStr,
        schedule: true,
      });
      toast.success('Job eingeplant');
      onCreated(createdShift);
    } catch (err: any) {
      pdebugError('Failed to create job:', err);
      toast.error(err?.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  }, [projectId, title, description, priority, teamId, dateStr, onCreated]);

  if (!isOpen) return null;

  const selectedTeam = teams.find((t) => t.id === teamId);
  const formattedDate = new Date(dateStr).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '500px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>
              Neuer Job
            </div>
            <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
              {selectedTeam?.name || 'Team'} • {formattedDate}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Projekt *
            </label>
            <select
              value={projectId || ''}
              onChange={(e) => setProjectId(e.target.value ? parseInt(e.target.value, 10) : null)}
              disabled={loadingProjects}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                background: 'white',
              }}
            >
              <option value="">
                {loadingProjects ? 'Lädt...' : 'Projekt auswählen...'}
              </option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.partner_name ? `${p.partner_name} – ` : ''}{p.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Titel *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Montage Waschbecken"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Beschreibung
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  marginBottom: '6px',
                }}
              >
                Priorität
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'NORMAL' | 'URGENT' | 'EMERGENCY')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: colors.bgCard,
                }}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  marginBottom: '6px',
                }}
              >
                Team
              </label>
              <select
                value={teamId}
                onChange={(e) => setTeamId(parseInt(e.target.value, 10))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: colors.bgCard,
                }}
              >
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Datum
            </label>
            <input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              style={{
                width: '200px',
                padding: '10px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '16px 20px',
            borderTop: `1px solid ${colors.border}`,
            gap: '12px',
          }}
        >
          <button
            onClick={onClose}
            style={{
              ...ps.buttonSecondary,
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...(saving ? ps.buttonDisabled : ps.buttonPrimary),
            }}
          >
            <FaSave size={12} />
            {saving ? 'Erstellen...' : 'Job erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
